import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { istDateKey } from '@/lib/ist'
import { useSyncStatus } from '@/offline'
import { useToast } from '@/ui'
import type { CommitDeliveryPayload } from '../commands'
import { closeDay, openDay } from '../data/actions'
import type { GateSession } from '../data/session'
import { useGate } from '../data/useGate'
import { istInstant } from '../model/codes'
import { reconcile } from '../model/day'
import type { PaperSlipHeader } from '../pages/delivery/DeliveryPage'
import {
  Avatar,
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  Keypad,
  Readout,
  Row,
  Strip,
  V1Header,
  V1Screen,
  V1Sheet,
  WaitCta,
} from './kit'
import { C, typeKey } from './style'
import { V1Photo } from './photo'
import { tr, fmtDate } from './text'
import { Rich } from './Rich'
import { V1DeliveryPage } from './V1Delivery'

/** A paper day has no times on its cover in V1: the day starts at 08:00 IST on the slips' date. */
const PAPER_DAY_START = '08:00'

function TypistCard() {
  const actor = useActor()
  return (
    <ItemRow
      padding="6px 10px"
      lead={<Avatar size={44} icon={37} />}
      titleSize={18}
      title={actor.name}
      line={<span style={{ fontSize: 15, color: C.muted }}>{tr('gate.v1.paper.typist')}</span>}
    />
  )
}

function DayStrip({ village, date }: { village: string; date: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 12,
        background: C.warnWash,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 10,
          background: C.surface,
          color: C.accDark,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="book" size={24} />
      </span>
      <span style={{ fontSize: 17, lineHeight: 1.35 }}>
        <Rich text={tr('gate.v1.paper.day', { village, date })} />
      </span>
    </div>
  )
}

/**
 * F4-B3 — काग़ज़ वाला दिन भरें: an office person types in a paper day with her own PIN within
 * 24 hours, slip by slip, on the same V1 screens (tags are scanned on every bag).
 */
export function V1PaperDay() {
  const g = useGate()
  const [typing, setTyping] = useState<PaperSlipHeader | null>(null)
  if (!g.ready || !g.roster) return <V1Screen />
  const s = g.paper
  if (!s) return <OpenPaperV1 />
  const draft = g.drafts.find(
    (x) => x.data.capture_mode === 'PAPER_BACKFILL' && x.data.session_id === s.id,
  )
  if (draft || typing)
    return <V1DeliveryPage paperSlip={typing ?? undefined} onPaperDone={() => setTyping(null)} />
  return <PaperHomeV1 session={s} onType={setTyping} />
}

function OpenPaperV1() {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const sync = useSyncStatus()
  const [today] = useState(() => istDateKey(Date.now()))
  const [date, setDate] = useState(() => istDateKey(Date.now() - 86_400_000))
  const [busy, setBusy] = useState(false)
  const reg = g.info.registration
  const opened = istInstant(date, PAPER_DAY_START)
  return (
    <V1Screen
      nav
      header={
        <V1Header
          onBack={() => navigate('/m/gate')}
          pending={sync.pending}
          icon="book"
          title={tr('gate.v1.paper.title')}
        />
      }
      footer={
        <Footer>
          {!opened || !reg ? (
            <WaitCta>{tr('gate.v1.paper.pickDay')}</WaitCta>
          ) : (
            <Cta
              label={tr('gate.v1.paper.open')}
              icon="book"
              busy={busy}
              onClick={async () => {
                if (!opened || !reg) return
                setBusy(true)
                try {
                  await openDay({
                    actorId: actor.user_id,
                    rosterVersion: g.info.rosterVersion,
                    receiptBookId: null,
                    rolls: [],
                    session: {
                      capture_mode: 'PAPER',
                      group_id: reg.team.id,
                      village_name: reg.team.name,
                      operator_id: actor.user_id,
                      operator_name: actor.name,
                      opened_at: opened,
                      scale_equipment_id: null,
                      scale_label: null,
                      stamp_ok: null,
                      test_weight_g: null,
                      test_weight_read_g: null,
                      tags_in_hand: 0,
                      seals_in_hand: 0,
                      paper: {
                        book_id: null,
                        book_no: null,
                        day_opened_at: opened,
                        day_closed_at: null,
                      },
                    },
                  })
                } catch (e) {
                  toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
                } finally {
                  setBusy(false)
                }
              }}
            />
          )}
        </Footer>
      }
    >
      <TypistCard />
      <DayStrip village={reg?.team.name ?? '—'} date={opened ? fmtDate(opened) : '—'} />
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 17, fontWeight: 700 }}>{tr('gate.v1.paper.pickDay')}</span>
        <input
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          style={{
            height: 52,
            padding: '0 12px',
            borderRadius: 12,
            border: `2px solid ${C.acc}`,
            background: C.surface,
            fontFamily: "'Mukta', sans-serif",
            fontSize: 20,
            fontWeight: 700,
            color: C.ink,
          }}
        />
      </label>
    </V1Screen>
  )
}

function PaperHomeV1({
  session,
  onType,
}: {
  session: GateSession
  onType: (h: PaperSlipHeader) => void
}) {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const sync = useSyncStatus()
  const [photo, setPhoto] = useState<string | null>(null)
  const [slipNo, setSlipNo] = useState<string | null>(null)
  const [sheet, setSheet] = useState(false)
  const [camera, setCamera] = useState(false)
  const [busy, setBusy] = useState(false)
  const typed = useMemo(
    () =>
      g.outbox
        .filter((r) => r.type === 'commit_delivery' && r.session_id === session.id)
        .map((r) => (r.payload as CommitDeliveryPayload).slip.slip_no),
    [g.outbox, session.id],
  )
  const next = useMemo(() => {
    const best = typed.reduce<string | null>((b, n) => (!b || Number(n) > Number(b) ? n : b), null)
    return best ? String(Number(best) + 1).padStart(best.length, '0') : null
  }, [typed])
  const no = slipNo ?? next
  const used = no !== null && typed.some((t) => Number(t) === Number(no))
  const opened = session.paper?.day_opened_at ?? session.opened_at
  // The time written on the slip (not the day's start): it is the lot's occurred_at and
  // what the late-entry check reads. It carries over to the next slip as a starting guess.
  const [time, setTime] = useState(PAPER_DAY_START)
  const slipTime = istInstant(istDateKey(Date.parse(opened)), time)
  const typeSlip = () => {
    if (!no || !photo || !slipTime) return
    onType({
      book_id: null,
      book_no: null,
      slip_no: no,
      slip_time: slipTime,
      page_photo_media_id: photo,
    })
  }
  /** The next thing to do: slip number, then the page photo, then the slip (tags are scanned there). */
  const goNext = () => {
    if (!no || used) setSheet(true)
    else if (!photo) setCamera(true)
    else typeSlip()
  }

  const close = async () => {
    setBusy(true)
    try {
      const rec = reconcile(g.outbox, session.id)
      await closeDay(session, actor.user_id, {
        tags_counted: null,
        seals_counted: null,
        lots_main: rec.lotsMain,
        lots_conv: rec.lotsConv,
        slip_range: null,
        closed_at: session.paper?.day_closed_at ?? undefined,
      })
      void sync.syncNow()
      navigate('/m/gate', { replace: true })
    } catch (e) {
      toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <V1Screen
      nav
      header={
        <V1Header
          onBack={() => navigate('/m/gate')}
          pending={sync.pending}
          icon="book"
          title={tr('gate.v1.paper.title')}
        />
      }
      footer={
        <Footer>
          <Row>
            <Btn icon="shutterdown" onClick={() => void close()} disabled={busy}>
              {tr('gate.v1.paper.close')}
            </Btn>
          </Row>
          {!no ? (
            <WaitCta onClick={goNext}>{tr('gate.v1.paper.slipNo')}</WaitCta>
          ) : used ? (
            <WaitCta onClick={goNext}>{tr('gate.v1.paperSlip.used')}</WaitCta>
          ) : !photo ? (
            <WaitCta onClick={goNext}>{tr('gate.v1.paper.pagePhoto')}</WaitCta>
          ) : !slipTime ? (
            <WaitCta>{tr('gate.v1.paper.slipTime')}</WaitCta>
          ) : (
            <Cta label={tr('gate.v1.paper.typeSlip', { no })} icon="next" onClick={typeSlip} />
          )}
        </Footer>
      }
      overlay={
        sheet ? (
          <SlipNoSheet
            value={no ?? ''}
            onClose={() => setSheet(false)}
            onSave={(v) => {
              setSlipNo(v)
              setSheet(false)
            }}
          />
        ) : null
      }
    >
      <TypistCard />
      <DayStrip village={session.village_name} date={fmtDate(opened)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}>
        <Icon name="slip" size={24} style={{ color: C.acc }} />
        <b style={{ fontSize: 19 }}>{tr('gate.v1.paper.typed', { count: typed.length })}</b>
      </div>
      <ItemRow
        padding="8px 10px"
        lead={
          <span
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 10,
              background: C.sunk,
              color: C.acc,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="slip" size={26} />
          </span>
        }
        title={no ? `${tr('gate.v1.paper.slipNo')} ${no}` : tr('gate.v1.paper.slipNo')}
        right={
          <div style={{ flex: '0 0 auto', display: 'flex' }}>
            <Btn icon="pencil" onClick={() => setSheet(true)}>
              {tr('gate.v1.change')}
            </Btn>
          </div>
        }
      />
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 17, fontWeight: 700 }}>{tr('gate.v1.paper.slipTime')}</span>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          style={{
            height: 52,
            padding: '0 12px',
            borderRadius: 12,
            border: `2px solid ${C.acc}`,
            background: C.surface,
            fontFamily: "'Mukta', sans-serif",
            fontSize: 20,
            fontWeight: 700,
            color: C.ink,
          }}
        />
      </label>
      <V1Photo
        kind="document"
        value={photo}
        onChange={setPhoto}
        open={camera}
        onOpenChange={setCamera}
        render={({ take, busy: shooting }) => (
          <ItemRow
            onClick={take}
            padding="8px 10px"
            lead={
              <span
                aria-hidden="true"
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: 10,
                  background: C.sunk,
                  color: C.acc,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="camera" size={26} />
              </span>
            }
            title={tr('gate.v1.paper.pagePhoto')}
            right={
              photo && !shooting ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 10px 3px 4px',
                    borderRadius: 16,
                    background: C.okWash,
                    color: C.ok,
                    fontWeight: 700,
                    fontSize: 16,
                    lineHeight: 1.25,
                    alignSelf: 'flex-start',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 11,
                      background: C.ok,
                      color: '#ffffff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name="ok" size={15} sw={3} />
                  </span>
                  {tr('gate.v1.paper.taken')}
                </span>
              ) : undefined
            }
          />
        )}
      />
      {/* Tags are scanned inside each slip: this takes her to the next step towards it. */}
      <Strip icon="scan" iconBox iconSize={24} onClick={goNext}>
        <Rich text={tr('gate.v1.paper.scanEach')} />
      </Strip>
    </V1Screen>
  )
}

function SlipNoSheet({
  value,
  onClose,
  onSave,
}: {
  value: string
  onClose: () => void
  onSave: (no: string) => void
}) {
  const [v, setV] = useState(value)
  const onKey = useCallback((k: string) => setV((c) => typeKey(c, k, { decimals: 0, max: 8 })), [])
  return (
    <V1Sheet
      title={tr('gate.v1.paper.slipNo')}
      onClose={onClose}
      top={120}
      actions={
        v ? (
          <Cta label={tr('gate.v1.save')} icon="ok" onClick={() => onSave(v)} />
        ) : (
          <WaitCta>{tr('gate.v1.paper.slipNo')}</WaitCta>
        )
      }
    >
      <Readout label={tr('gate.v1.paper.slipNo')} value={v} size={32} />
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Sheet>
  )
}
